/* Small SVG charts for PostHog numbers: a sparkline for rows, daily bars for a metric, and funnel step bars.
   Shapes stretch to the container (viewBox + preserveAspectRatio="none"); text is HTML so it never stretches.
   Hovering a bar shows its day and value via the SVG <title>. Colors are theme tokens (light + dark). */

const VIEW_W = 100
const SPARK_H = 24
const BARS_H = 64
const BAR_GAP_RATIO = 0.2
const MIN_BAR_H = 0.6
const PCT = 100

const SERIES_COLORS = ["--chart-1", "--chart-2", "--chart-4", "--chart-5"]
export const seriesColor = (i: number) => `hsl(var(${SERIES_COLORS[i % SERIES_COLORS.length]}))`

const maxOf = (values: number[]) => Math.max(1, ...values)

/** Trend at a glance; the last point (today so far) is dotted so a partial day doesn't read as a drop */
export function Sparkline({
  values,
  color = seriesColor(0),
  className = "h-6 w-24",
}: {
  values: number[]
  color?: string
  className?: string
}) {
  if (values.length < 2) return null
  const max = maxOf(values)
  const x = (i: number) => (i / (values.length - 1)) * VIEW_W
  const y = (v: number) => SPARK_H - (v / max) * (SPARK_H - 2) - 1
  const points = values.map((v, i) => `${x(i)},${y(v)}`)
  const lastIdx = values.length - 1
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${SPARK_H}`}
      preserveAspectRatio="none"
      className={className}
      role="img"
      aria-label={`Trend: ${values.join(", ")}`}
    >
      <polyline
        points={points.slice(0, lastIdx).join(" ")}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points={points.slice(lastIdx - 1).join(" ")}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="2 2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/** One bar per day; today (partial) is drawn lighter */
export function DailyBars({ days, values, label }: { days: string[]; values: number[]; label: string }) {
  const max = maxOf(values)
  const slot = VIEW_W / Math.max(1, values.length)
  return (
    <div>
      <svg
        viewBox={`0 0 ${VIEW_W} ${BARS_H}`}
        preserveAspectRatio="none"
        className="h-16 w-full"
        role="img"
        aria-label={`${label} per day`}
      >
        {values.map((v, i) => {
          const h = Math.max(MIN_BAR_H, (v / max) * BARS_H)
          const today = i === values.length - 1
          return (
            <rect
              key={days[i] ?? i}
              x={i * slot + (slot * BAR_GAP_RATIO) / 2}
              y={BARS_H - h}
              width={slot * (1 - BAR_GAP_RATIO)}
              height={h}
              fill={v === 0 ? "hsl(var(--muted-foreground) / 0.25)" : seriesColor(0)}
              opacity={today ? 0.5 : 1}
            >
              <title>{`${days[i] ?? ""}: ${v.toLocaleString()} ${label}${today ? " (so far)" : ""}`}</title>
            </rect>
          )
        })}
      </svg>
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span>{days[0]}</span>
        <span>today</span>
      </div>
    </div>
  )
}

export const stepPct = (count: number, first: number) => (first > 0 ? Math.round((count / first) * PCT) : 0)

/** Horizontal bars, each step as a share of the first, with step-to-step conversion */
export function FunnelBars({ steps }: { steps: { label: string; count: number }[] }) {
  const first = steps[0]?.count ?? 0
  return (
    <ol className="space-y-1.5">
      {steps.map((step, i) => {
        const prev = steps[i - 1]
        const pct = stepPct(step.count, first)
        return (
          <li key={`${i}:${step.label}`} className="text-xs">
            <div className="flex justify-between gap-2">
              <span className="truncate">{step.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {step.count.toLocaleString()} · {pct}%
                {prev && prev.count > 0 ? ` · ${stepPct(step.count, prev.count)}% of prev` : ""}
              </span>
            </div>
            <div className="mt-0.5 h-1.5 rounded bg-secondary">
              <div className="h-1.5 rounded" style={{ width: `${pct}%`, background: seriesColor(0) }} />
            </div>
          </li>
        )
      })}
    </ol>
  )
}
